import React, { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "polyrouter";
import { MenuItem } from "store/menu";
import { State } from "store";
import { SearchInput, useFeatureFlagWithTenant } from "elements";
import logo from "assets/images/poly_logo_lens.svg";
import { AdminMenu, Menu, TenantSwitcher } from "collections/Menu";
import { isMobile } from "react-device-detect";
import { useRouteMatch } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { useNotification } from "collections";
import { useTranslation } from "utilities/i18n";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { SupportMenu } from "collections/Menu/SupportMenu";
import { MOBILE_BETA_LINK } from "Constants";
import { Emoji } from "utilities/emoji";
import "./Topbar.scss";

export const Topbar: React.FC = () => {
  const items: MenuItem[] = useSelector((state: State) => state.menu.items);
  const { user } = useAuth0();
  const match = useRouteMatch(["/manage/inventory/map"]);
  const completeProfileMatch = useRouteMatch(["/complete-profile"]);
  const isPathAcceptInvite = useRouteMatch(["/accept-invite"]);
  const classes = [
    "topbar",
    "grid",
    "closed",
    isMobile ? "mobile" : "",
    match?.isExact ? "opaque" : ""
  ]
    .filter(c => !!c)
    .join(" ");
  const { t } = useTranslation();

  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const [addNotification] = useNotification();
  const [motdToastEnabled] = useFeatureFlagWithTenant("MOTD_TOAST_ENABLED");
  const [mobileAppToastEnabled] = useFeatureFlagWithTenant(
    "MOBILE_APP_REGISTRATION_TOAST"
  );

  useEffect(() => {
    if (tenantId && motdToastEnabled) {
      if (mobileAppToastEnabled) {
        addNotification({
          id: "beta",
          message: (
            <div className="beta-text">
              <div>{t("THE_LENS_MOBILE_APPLICATION_BETA")}</div>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={MOBILE_BETA_LINK}
              >
                {t("REGISTER_HERE")}
                <Emoji label={"fire"} symbol={"🔥"} />
              </a>
            </div>
          ),
          type: "beta",
          dismiss: 30000 // 30 seconds
        });
      }
    }
    // omit addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, motdToastEnabled, mobileAppToastEnabled]);
  return (
    <div className={classes}>
      <div className="col-12 col-md-6 text-left brand pl-25 pt-2">
        <Link to="/home" className="d-inline-block">
          <img src={logo} alt="Poly Lens" data-testid="logo" />
        </Link>
        {isPathAcceptInvite?.isExact || completeProfileMatch != null ? null : (
          <>
            {isMobile ? null : (
              <Menu className="d-inline-block mt-0" items={items} />
            )}
          </>
        )}
      </div>
      {isPathAcceptInvite?.isExact || completeProfileMatch != null ? null : (
        <div className="d-none-xs d-inline-block-sm col-6 text-right right pr-2">
          <ul className="relative-children">
            <li className="d-inline-block-md account-menu-item">
              <TenantSwitcher />
            </li>
            <li className="d-none-xs d-inline-block-md search-menu-item">
              <SearchInput />
            </li>
            <li className="d-none-xs d-inline-block-md support-menu-item">
              <SupportMenu />
            </li>
            <li className="admin-menu-item">
              <AdminMenu user={user} />
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};
